import "./DashboardPayroll.css";
import { useState, useEffect, useRef, useMemo } from "react";
import { MultiSelect } from "primereact/multiselect";
import {
  getPayrollByProjectListRequest,
  getUserPayrollFromPeriod,
} from "../../services/wisdomServices/reportService";
import { requestCreateAuditLog } from "../../services/wisdomServices/auditService";
import { getSessionItem } from "../../utils/helper/session";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { FloatLabel } from "primereact/floatlabel";
import moment from "moment";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { downloadPayrollWorkbook } from "../../utils/helper/exports";
import { Can, PERMISSIONS } from "../../security";

function PayrollDetails() {
  const multiSelectRef = useRef(null);

  const [userPayrollData, setUserPayrollData] = useState([]);
  const [expandedDataMap, setExpandedDataMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [dates, setDates] = useState(null);
  const [expandedRows, setExpandedRows] = useState(null);
  const [expandedWeekRowsMap, setExpandedWeekRowsMap] = useState({});
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const user = getSessionItem("User");

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.EQUALS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    username: { value: null, matchMode: FilterMatchMode.CONTAINS },
    project: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const projects = useMemo(() => {
    return getSessionItem("projects") || [];
  }, []);

  const getProjectIdByName = (projectName) => {
    return projects.find((x) => x.name === projectName)?.id;
  };

  const getPayrollRowKey = (row) => {
    return `${row.id}-${row.project}`;
  };

  const formatFromDate = () => moment(dates[0]).format("yyyy-MM-DD");
  const formatToDate = () => moment(dates[1]).format("yyyy-MM-DD");

  useEffect(() => {
    const handleScroll = (event) => {
      const panel = document.querySelector(".p-multiselect-panel");
      if (!panel) return;
      if (panel.contains(event.target)) return;
      multiSelectRef.current?.hide();
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;

    setFilters((prev) => ({
      ...prev,
      global: {
        ...prev.global,
        value,
      },
    }));

    setGlobalFilterValue(value);
  };

  const getPayrollDetails = async () => {
    setUserPayrollData([]);
    setExpandedRows(null);
    setExpandedDataMap({});
    setExpandedWeekRowsMap({});

    const projectIds = selectedProjects;

    try {
      const response = await getPayrollByProjectListRequest(
        formatFromDate(),
        formatToDate(),
        projectIds
      );

      const dataWithKeys = response.map((item) => ({
        ...item,
        rowKey: getPayrollRowKey(item),
      }));

      setUserPayrollData(dataWithKeys);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onRowExpand = async (event) => {
    const row = event.data;
    const rowKey = row.rowKey;

    const projectId = getProjectIdByName(row.project);
    if (!projectId) return;

    try {
      const response = await getUserPayrollFromPeriod(
        row.id,
        projectId,
        formatFromDate(),
        formatToDate()
      );

      setExpandedDataMap((prev) => ({
        ...prev,
        [rowKey]: response,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const expandAll = async () => {
    try {
      setLoading(true);

      const newExpandedRows = {};
      const newExpandedDataMap = {};

      for (const row of userPayrollData) {
        const rowKey = row.rowKey;

        const projectId = getProjectIdByName(row.project);
        if (!projectId) continue;

        const weeks = await getUserPayrollFromPeriod(
          row.id,
          projectId,
          formatFromDate(),
          formatToDate()
        );

        newExpandedRows[rowKey] = true;
        newExpandedDataMap[rowKey] = weeks;
      }

      setExpandedDataMap(newExpandedDataMap);
      setExpandedRows(newExpandedRows);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const collapseAll = () => {
    setExpandedRows(null);
    setExpandedDataMap({});
    setExpandedWeekRowsMap({});
  };

  const logDownloadAction = async () => {
    try {
      const log = {
        action: "download",
        entityType: "report",
        entityId: "payroll",
        details: `[Requested by ${user.firstName} ${user.lastName}] - from: ${formatFromDate()}, to: ${formatToDate()}`,
      };

      await requestCreateAuditLog(log);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAllExpandedData = async () => {
    const full = [];

    for (const row of userPayrollData) {
      const projectId = getProjectIdByName(row.project);
      if (!projectId) continue;

      const weeks = await getUserPayrollFromPeriod(
        row.id,
        projectId,
        formatFromDate(),
        formatToDate()
      );

      full.push({
        ...row,
        weeks,
      });
    }

    return full;
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);

      const fullData = await fetchAllExpandedData();

      downloadPayrollWorkbook(fullData, "Payroll_Report.xlsx");

      await logDownloadAction();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    if (!dates || !dates[0] || !dates[1]) return;

    setLoading(true);
    await getPayrollDetails();
  };

  const periodTemplateColumn = (rowData) => {
    if (Object.hasOwn(rowData, "weekNumber")) {
      return <Tag value={rowData.weekNumber} severity="info" />;
    }

    return <Tag value={rowData.date} severity="success" />;
  };

  const userIdTemplateColumn = (rowData) => {
    return <Tag value={rowData.id} severity="warning" />;
  };

  const nameTemplateColumn = (rowData) => {
    return <Tag value={rowData.name} severity="warning" />;
  };

  const weekRowExpansionTemplate = (rowData) => {
    return (
      <div className="p-3">
        <DataTable
          value={rowData.children}
          dataKey="date"
          className="payroll-compact-table"
        >
          <Column expander={false} style={{ width: "5rem" }} />
          <Column header="Day" body={periodTemplateColumn} />
          <Column field="activeTime" header="Tracked Time" />
          <Column field="idleTime" header="Idle Time" />
          <Column field="markedCards" header="Marked Cards" />
          <Column field="requested" header="Manual Time" />
          <Column field="approvedOT" header="Approved OT" />
          <Column field="overtime" header="Overtime" />
          <Column field="rejectedTime" header="Rejected" />
          <Column field="payroll" header="Payroll Time" />
        </DataTable>
      </div>
    );
  };

  const rowExpansionTemplate = (rowData) => {
    const rowKey = rowData.rowKey;
    const weeks = expandedDataMap[rowKey];

    if (!weeks) {
      return <p style={{ padding: "1rem" }}>Loading...</p>;
    }

    return (
      <div className="p-3">
        <DataTable
          className="payroll-compact-table"
          value={weeks}
          dataKey="weekNumber"
          expandedRows={expandedWeekRowsMap[rowKey] || null}
          onRowToggle={(e) => {
            setExpandedWeekRowsMap((prev) => ({
              ...prev,
              [rowKey]: e.data,
            }));
          }}
          rowExpansionTemplate={weekRowExpansionTemplate}
        >
          <Column expander={true} style={{ width: "5rem" }} />
          <Column header="Week" body={periodTemplateColumn} />
          <Column field="activeTime" header="Tracked Time" />
          <Column field="idleTime" header="Idle Time" />
          <Column field="markedCards" header="Marked Cards" />
          <Column field="requested" header="Manual Time" />
          <Column field="approvedOT" header="Approved OT" />
          <Column field="overtime" header="Overtime" />
          <Column field="rejectedTime" header="Rejected" />
          <Column field="payroll" header="Payroll Time" />
        </DataTable>
      </div>
    );
  };

  const header = (
    <div className="payroll-table-header">
      <div className="flex flex-wrap justify-content-end gap-2">
        <Button
          icon="pi pi-plus"
          label="Expand All"
          onClick={expandAll}
          text
          disabled={userPayrollData.length <= 0}
        />

        <Button
          icon="pi pi-minus"
          label="Collapse All"
          onClick={collapseAll}
          text
          disabled={userPayrollData.length <= 0}
        />

        <Button
          icon="pi pi-file-excel"
          label="Export"
          onClick={handleExportCSV}
          disabled={userPayrollData.length <= 0}
        />
      </div>

      <div className="flex justify-content-end">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />

          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Keyword Search"
          />
        </IconField>
      </div>
    </div>
  );

  if (!projects || projects.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          textAlign: "center",
        }}
      >
        <h1 style={{ color: "#999", fontWeight: 400 }}>
          You don’t have any assigned projects yet to access{" "}
          <strong>Payroll</strong> page.
        </h1>
      </div>
    );
  }

  return (
    <div
      className="dashboardPayrollMain"
      style={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      <h1 className="dashboardPayrollMain-header">Payroll Details</h1>

      <div className="payroll-form">
        <div className="payroll-details-date-range-selector">
          <FloatLabel>
            <Calendar
              inputId="payroll-date-range-selector"
              value={dates}
              onChange={(e) => setDates(e.value)}
              selectionMode="range"
              readOnlyInput
              hideOnRangeSelection
              selectOtherMonths={true}
            />

            <label htmlFor="payroll-date-range-selector">
              Select a date range
            </label>
          </FloatLabel>
        </div>

        <div className="manager-dash-project-multiselect">
          <div className="project-multiselect-with-spinner">
            <MultiSelect
              ref={multiSelectRef}
              value={selectedProjects}
              filter
              filterDelay={400}
              filterPlaceholder="Search..."
              onChange={(e) => setSelectedProjects(e.value)}
              options={projects}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Projects"
              maxSelectedLabels={3}
              className="w-full md:w-20rem"
            />
          </div>
        </div>
        <Can permission={PERMISSIONS.reportsPayroll_get}>
          <Button
            severity="info"
            icon="pi pi-check"
            label="Generate"
            onClick={generate}
            disabled={selectedProjects.length <= 0 || !dates || !dates[0] || !dates[1]}
          />
        </Can>
      </div>

      <div className="payroll-page-card">
        <DataTable
          dataKey="rowKey"
          className="payroll-compact-table"
          value={userPayrollData}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          onRowExpand={onRowExpand}
          header={header}
          loading={loading}
          filters={filters}
          filterDelay={400}
          globalFilterFields={["id", "username", "name", "project"]}
          rowExpansionTemplate={rowExpansionTemplate}
        >
          <Column expander={true} style={{ width: "5rem" }} />
          <Column header="User ID" body={userIdTemplateColumn} />
          <Column field="username" header="Email" />
          <Column header="Name" body={nameTemplateColumn} />
          <Column field="project" header="Project" />
          <Column field="activeTime" header="Tracked Time" />
          <Column field="idleTime" header="Idle Time" />
          <Column field="markedCards" header="Marked Cards" />
          <Column field="requested" header="Manual Time" />
          <Column field="approvedOT" header="Approved OT" />
          <Column field="overtime" header="Overtime" />
          <Column field="rejectedTime" header="Rejected" />
          <Column field="payroll" header="Payroll Time" />
        </DataTable>
      </div>
    </div>
  );
}

export default PayrollDetails;